<script lang="ts">
  import { untrack } from 'svelte';
  import { WidgetApiClient, ApiError } from './lib/api-client';
  import { RealtimeSocket } from './lib/socket';
  import { loadSession, saveSession } from './lib/session-store';
  import { t } from './lib/i18n';
  import { collectPage } from './lib/page-context';
  import { readSourced } from './lib/data-sources';
  import { wantsHumanAgent } from './lib/human-intent';
  import { decideOfferHandoff } from './lib/escalation-signals';
  import { playNotificationSound } from './lib/notification-sound';
  import { mapRealtimeMessageEvent } from './lib/realtime-mappers';
  import type {
    Attachment,
    Branding,
    CaptureConfig,
    CaptureFieldName,
    Faq,
    HandoffContact,
    Locale,
    Message,
    ScriptStep,
    ScriptTrigger,
    SessionClaims,
    SessionPayload,
    ViewName,
    WidgetAnimation,
    WidgetPosition,
    WidgetShape,
    WidgetSize,
  } from './lib/types';
  import { isBuiltinHandoffField } from './lib/types';

  import FloatingButton from './ui/FloatingButton.svelte';
  import Panel from './ui/Panel.svelte';
  import FaqList from './ui/FaqList.svelte';
  import WelcomeChips from './ui/WelcomeChips.svelte';
  import ChatThread from './ui/ChatThread.svelte';
  import Composer from './ui/Composer.svelte';
  import HandoffForm from './ui/HandoffForm.svelte';
  import LeadCaptureForm from './ui/LeadCaptureForm.svelte';
  import ScriptChoices from './ui/ScriptChoices.svelte';
  import WelcomeBubble from './ui/WelcomeBubble.svelte';

  interface Props {
    serviceId?: string;
    jwt?: string;
    apiUrl?: string;
    realtimeUrl?: string;
    locale?: Locale;
    primaryColorOverride?: string;
    logoUrlOverride?: string;
    positionOverride?: WidgetPosition;
    greetingOverride?: string;
    assistantNameOverride?: string;
    assistantAvatarOverride?: string;
    assistantTaglineOverride?: string;
    widgetShapeOverride?: WidgetShape;
    widgetSizeOverride?: WidgetSize;
    widgetAnimationOverride?: WidgetAnimation;
    widgetImageOverride?: string;
    welcomeMessageOverride?: string;
    welcomeTimeoutOverride?: number;
  }

  let {
    serviceId = '',
    jwt = '',
    apiUrl = 'http://localhost:5173',
    realtimeUrl = 'ws://localhost:4000',
    locale: localeProp,
    primaryColorOverride,
    logoUrlOverride,
    positionOverride,
    greetingOverride,
    assistantNameOverride,
    assistantAvatarOverride,
    assistantTaglineOverride,
    widgetShapeOverride,
    widgetSizeOverride,
    widgetAnimationOverride,
    widgetImageOverride,
    welcomeMessageOverride,
    welcomeTimeoutOverride,
  }: Props = $props();

  const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024; // 25 MB
  const DEFAULT_PRIMARY = '#2563eb';
  const TEMPORARY_DISMISS_MS = 30_000;
  const SERVICE_LANDING_URL = 'https://helpdesk.papaya.com.pe';

  // ---- Reactive state -------------------------------------------------------

  let isOpen = $state(false);
  let view = $state<ViewName>('faq');
  let isLoading = $state(true);
  let errorMessage = $state<string | null>(null);

  // In-memory only — when the visitor closes the FAB we hide the widget for a
  // short cooldown so it stops covering page controls, then show it again.
  let dismissed = $state(false);
  let widgetDismissTimer: ReturnType<typeof setTimeout> | null = null;

  // Welcome bubble visibility. We start hidden and flip to true once the
  // session loads + we know the configured message; auto-hide via $effect
  // below. Closing the bubble or opening the panel both clear it. The
  // setTimeout handle lives inside the $effect closure so Svelte's
  // automatic cleanup guarantees no leaks across re-runs or unmount.
  let welcomeVisible = $state(false);
  let welcomeDismissed = $state(false);
  let welcomeDismissTimer: ReturnType<typeof setTimeout> | null = null;

  // Per-session mute toggle. Visitor-controlled via the panel header. The
  // tenant-wide default lives in `branding.notification_sound` (default on).
  let soundMuted = $state(false);

  /** Beep on every assistant or human-agent reply, unless muted. */
  function maybePlayReplySound(role: Message['role']): void {
    if (role !== 'ai' && role !== 'agent') return;
    if (soundMuted) return;
    if (branding.notification_sound === false) return;
    playNotificationSound();
  }

  let branding = $state<Branding>({
    locale: 'en',
    position: 'bottom-right',
  });
  let faqs = $state<Faq[]>([]);
  let tenantId = $state<string | null>(null);
  let messages = $state<Message[]>([]);
  let conversationId = $state<string | null>(null);
  let pendingAttachments = $state<Attachment[]>([]);
  let pendingFileUrls = new Map<string, string>();

  let isTyping = $state(false);
  let agentJoined = $state(false);
  let humanConnecting = $state(false);
  let unread = $state(0);
  // Tenant ran out of plan quota and a brand-new conversation is rejected
  // server-side. We disable the composer and surface a generic notice — we
  // never expose plan / billing details to the end customer.
  let serviceBlocked = $state(false);

  // Identity / handoff state. `capture` arrives with the session payload; may
  // be `null` against an older dashboard that doesn't yet ship the field.
  let capture = $state<CaptureConfig | null>(null);
  let sessionClaims = $state<SessionClaims | null>(null);
  // Values read from cookies / localStorage / window after `capture.sources`
  // is known. Used as a passive backup to skip the handoff form and as a
  // fallback merged into the contact payload when the form is skipped.
  let sourced = $state<Record<string, string>>({});
  let handoffError = $state<string | null>(null);
  let handoffSubmitting = $state(false);
  // Inline handoff collection: when the tenant has a handoff form configured,
  // we collect the required fields through regular chat messages instead of
  // popping a modal. `null` means not collecting; otherwise we're mid-flow.
  let handoffCollecting = $state<HandoffContact | null>(null);

  // Lead capture state.
  let leadError = $state<string | null>(null);
  let leadSubmitting = $state(false);
  let leadCaptured = $state(false);
  // How many non-ai turns the customer has sent since this session mounted.
  // Drives the `after_n_turns` trigger; reset when a new conversation starts.
  let customerTurnCount = $state(0);

  // Soft handoff proposal: set by the server when the LLM suggested an
  // escalation but didn't meet the hard-trigger conditions. While true, the
  // next user message is inspected for an affirmative reply ("sí", "dale",
  // "yes"); affirmative → fire handoff, non-affirmative → clear the flag
  // and keep chatting as normal.
  let handoffProposalPending = $state(false);

  // Inline Yes/No handoff offer. When the client detects the AI is failing
  // (user frustration phrases, bot refusal language, or low confidence
  // repeating) the widget appends a synthetic "¿Quieres que te pase con un
  // asesor?" message with two buttons. `handoffOfferPending=true` surfaces
  // the buttons below the thread until the user decides.
  let handoffOfferPending = $state(false);
  // True while the offer is visible, suppressing further auto-offers so we
  // don't pile them up across turns.
  let handoffOfferShownThisSession = $state(false);

  // Scripted flow state. When `currentStepId` is non-null, the widget short-
  // circuits the AI pipeline entirely: the user sees pre-programmed prompts
  // and their answers accumulate locally until the final step, at which
  // point everything is POSTed as a single lead.
  let scriptState = $state<{
    currentStepId: string | null;
    answers: Record<string, string>;
    trigger: ScriptTrigger | null;
    completed: boolean;
    submitting: boolean;
    /** When true, the script was started with `onHandoff` and we need to
     *  trigger the handoff once the flow completes. Tracked separately from
     *  `handoffAfter` so handoff always runs when the trigger was onHandoff,
     *  even if the config later flips `handoffAfter` off. */
    handoffPending: boolean;
  }>({
    currentStepId: null,
    answers: {},
    trigger: null,
    completed: false,
    submitting: false,
    handoffPending: false,
  });

  let apiClient: WidgetApiClient | null = null;
  let socket: RealtimeSocket | null = null;
  let streamAbort: AbortController | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let lastPollIso: string = new Date(0).toISOString();

  const activeLocale = $derived<Locale>(localeProp ?? branding.locale ?? 'en');

  const brandingStyle = $derived(buildBrandingStyle(branding));
  const positionStyle = $derived(buildPositionStyle(branding.position));

  // True when the tenant has configured a handoff form with at least one
  // enabled field. When false we always skip the form and hand off directly.
  const handoffFormAvailable = $derived(
    !!capture?.handoff?.enabled &&
      (capture?.handoff?.fields ?? []).some((f) => f.enabled),
  );

  /**
   * Seed contact derived from whatever identity we already know about the
   * customer: verified JWT claims first, then passive `sourced` hints from
   * cookies / localStorage / window. Used as the starting point for the
   * handoff form or the inline collection flow so we don't ask for fields
   * we already have.
   */
  const seededContact = $derived<HandoffContact>({
    name: sessionClaims?.nam ?? sourced.name,
    email: sessionClaims?.eml ?? sourced.email,
    business: sourced.business,
    phone: sourced.phone,
  });

  // True only when EVERY required handoff field is already covered by the
  // seeded contact. This replaces the old over-permissive `identityResolved`
  // check (which fired as soon as JWT.met had any key, even unrelated ones),
  // so the widget no longer skips collection when a required field is still
  // missing.
  const allRequiredFieldsCovered = $derived(
    handoffFormAvailable ? missingHandoffFields(seededContact).length === 0 : true,
  );

  // --- Scripted flow -----------------------------------------------------
  const scriptConfig = $derived(capture?.script);
  const scriptEnabled = $derived(
    !!scriptConfig?.enabled &&
      (scriptConfig?.steps?.length ?? 0) > 0 &&
      !!scriptConfig?.firstStepId,
  );
  const scriptActive = $derived(scriptState.currentStepId !== null);
  const currentScriptStep = $derived.by<ScriptStep | null>(() => {
    const id = scriptState.currentStepId;
    if (!id || !scriptConfig) return null;
    return scriptConfig.steps.find((s) => s.id === id) ?? null;
  });
  const scriptAwaitingChoice = $derived(
    currentScriptStep?.type === 'choice' && !scriptState.submitting,
  );
  const scriptAwaitingText = $derived(
    currentScriptStep?.type === 'text' && !scriptState.submitting,
  );

  // Lead capture surfaces only when the tenant configured it AND at least
  // one field is declared. The trigger dispatch lives further down.
  const leadCaptureAvailable = $derived(
    !!capture?.leads?.enabled && (capture?.leads?.fields ?? []).length > 0,
  );

  // ---- Lifecycle ------------------------------------------------------------

  $effect(() => {
    if (!serviceId || !jwt) {
      errorMessage = 'Missing service-id or jwt attributes.';
      view = 'error';
      isLoading = false;
      return;
    }

    const cached = loadSession(serviceId);
    if (cached) {
      conversationId = cached.conversation_id;
      messages = cached.messages;
    }

    apiClient = new WidgetApiClient({ baseUrl: apiUrl, jwt });
    void initSession();

    return () => {
      teardownRealtime();
      streamAbort?.abort();
      if (widgetDismissTimer) clearTimeout(widgetDismissTimer);
      if (welcomeDismissTimer) clearTimeout(welcomeDismissTimer);
      for (const url of pendingFileUrls.values()) {
        URL.revokeObjectURL(url);
      }
      pendingFileUrls.clear();
    };
  });

  // Persist session whenever messages or conversationId change.
  $effect(() => {
    if (!serviceId) return;
    saveSession(serviceId, {
      conversation_id: conversationId,
      messages,
      updated_at: Date.now(),
    });
  });

  // Merge playground/preview overrides into branding whenever they change.
  // Reads `branding` via untrack to avoid self-triggering the effect.
  $effect(() => {
    // Explicit reads so the effect tracks override dependencies.
    const pc = primaryColorOverride;
    const lu = logoUrlOverride;
    const pos = positionOverride;
    const gr = greetingOverride;
    const an = assistantNameOverride;
    const av = assistantAvatarOverride;
    const at = assistantTaglineOverride;
    const ws = widgetShapeOverride;
    const wsz = widgetSizeOverride;
    const wa = widgetAnimationOverride;
    const wi = widgetImageOverride;
    const wm = welcomeMessageOverride;
    const wt = welcomeTimeoutOverride;
    untrack(() => {
      branding = {
        ...branding,
        ...(pc ? { primary_color: pc } : {}),
        ...(lu ? { logo_url: lu } : {}),
        ...(pos ? { position: pos } : {}),
        ...(gr !== undefined ? { greeting: gr } : {}),
        ...(an !== undefined ? { assistant_name: an } : {}),
        ...(av !== undefined ? { assistant_avatar_url: av } : {}),
        ...(at !== undefined ? { assistant_tagline: at } : {}),
        ...(ws ? { widget_shape: ws } : {}),
        ...(wsz ? { widget_size: wsz } : {}),
        ...(wa ? { widget_animation: wa } : {}),
        ...(wi !== undefined ? { widget_image_url: wi } : {}),
        ...(wm !== undefined ? { welcome_message: wm } : {}),
        ...(wt !== undefined ? { welcome_timeout: wt } : {}),
      };
    });
  });

  async function initSession(): Promise<void> {
    if (!apiClient) return;
    isLoading = true;
    errorMessage = null;
    try {
      // First session has empty `sourced` — we only know which cookies /
      // localStorage keys / window paths to read once `capture.sources`
      // arrives in the response. For handoff-form-skip purposes the sourced
      // object is computed post-session and used at request-human time.
      // First session's metadata gets no sourced branch; that's fine since
      // sourced is defense-in-depth anyway.
      const payload: SessionPayload = await apiClient.session({});
      tenantId = payload.tenantId;
      capture = payload.capture ?? null;
      sessionClaims = payload.claims ?? null;
      sourced = capture ? readSourced(capture.sources) : {};
      branding = {
        ...payload.branding,
        ...(primaryColorOverride ? { primary_color: primaryColorOverride } : {}),
        ...(logoUrlOverride ? { logo_url: logoUrlOverride } : {}),
        ...(positionOverride ? { position: positionOverride } : {}),
        ...(greetingOverride !== undefined ? { greeting: greetingOverride } : {}),
        ...(assistantNameOverride !== undefined
          ? { assistant_name: assistantNameOverride }
          : {}),
        ...(assistantAvatarOverride !== undefined
          ? { assistant_avatar_url: assistantAvatarOverride }
          : {}),
        ...(assistantTaglineOverride !== undefined
          ? { assistant_tagline: assistantTaglineOverride }
          : {}),
        ...(widgetShapeOverride ? { widget_shape: widgetShapeOverride } : {}),
        ...(widgetSizeOverride ? { widget_size: widgetSizeOverride } : {}),
        ...(widgetAnimationOverride
          ? { widget_animation: widgetAnimationOverride }
          : {}),
        ...(widgetImageOverride !== undefined
          ? { widget_image_url: widgetImageOverride }
          : {}),
        ...(welcomeMessageOverride !== undefined
          ? { welcome_message: welcomeMessageOverride }
          : {}),
        ...(welcomeTimeoutOverride !== undefined
          ? { welcome_timeout: welcomeTimeoutOverride }
          : {}),
      };
      faqs = payload.faqs ?? [];
      serviceBlocked = payload.blocked === true;

      if (payload.conversation) {
        conversationId = payload.conversation.id;
        messages = payload.conversation.messages ?? messages;
        // Seed lastPollIso from the freshest message we restored, otherwise
        // the first poll would re-download the entire thread (since=epoch).
        for (const m of messages) {
          if (m.created_at && m.created_at > lastPollIso) {
            lastPollIso = m.created_at;
          }
        }
        if (
          payload.conversation.status === 'assigned' ||
          payload.conversation.status === 'awaiting_human'
        ) {
          agentJoined = payload.conversation.status === 'assigned';
        }
      }

      setupRealtime();
      view = messages.length > 0 ? 'chat' : 'faq';

      // If the tenant configured an on-open script and the user hasn't already
      // completed it this session, kick it off now. We intentionally only run
      // it when the thread is empty — on resume the messages already show the
      // in-progress script state (we rely on localStorage-persisted messages).
      if (messages.length === 0) {
        startScript('on_open');
      }
    } catch (err) {
      errorMessage = errorToMessage(err, activeLocale);
      view = 'error';
    } finally {
      isLoading = false;
    }
  }

  function setupRealtime(): void {
    if (!tenantId || !jwt) return;
    teardownRealtime();

    socket = new RealtimeSocket(
      { realtimeUrl, tenantId, jwt },
      {
        onMessage: (msg) => {
          if (!conversationId || msg.conversationId !== conversationId) return;
          const mapped = mapRealtimeMessageEvent(msg).message;
          // Ignore own-customer echoes — the server broadcasts the
          // customer's message back via realtime, but the widget already
          // appended it locally when the user pressed Send. The DB id
          // and the local `cryptoRandomId` don't match so the dedup-by-id
          // check in `appendMessage` would otherwise produce a duplicate
          // bubble (the user's question reappearing seconds later).
          if (mapped.role === 'customer') return;
          const inserted = appendMessage(mapped);
          if (inserted) {
            if (!isOpen) {
              unread += 1;
            }
            maybePlayReplySound(mapped.role);
          }
        },
        onAgentJoined: (payload) => {
          agentJoined = true;
          humanConnecting = false;
          view = 'chat';
          appendSystem(
            t(activeLocale, 'chat.agent_joined', {
              name: payload.agentName ?? 'An agent',
            }),
          );
        },
        onStatusChanged: (payload) => {
          if (payload.status === 'assigned') {
            humanConnecting = false;
            agentJoined = true;
          } else if (payload.status === 'awaiting_human') {
            humanConnecting = true;
          }
        },
        onTyping: (payload) => {
          isTyping = payload.isTyping;
        },
        onFallback: () => {
          startPolling();
        },
      }
    );
    socket.connect();
  }

  function teardownRealtime(): void {
    socket?.disconnect();
    socket = null;
    stopPolling();
  }

  /**
   * Start the polling fallback. We deliberately gate it on "there is a
   * human in the loop" — when the visitor is just chatting with the AI,
   * replies arrive through SSE streaming and polling adds zero value.
   * Polling against `/api/widget/conversations/:id/messages` from a
   * cross-origin embed can otherwise burn ~1200 reqs/hour for nothing.
   *
   * The interval is 8s (was 3s): a fallback for the rare case where
   * Socket.IO can't connect, not a primary delivery channel.
   */
  function startPolling(): void {
    if (pollTimer || !apiClient || !conversationId) return;
    if (!agentJoined && !humanConnecting) return;
    pollTimer = setInterval(() => {
      void pollOnce();
    }, 8000);
  }

  function stopPolling(): void {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
  }

  async function pollOnce(): Promise<void> {
    if (!apiClient || !conversationId) return;
    try {
      const { messages: incoming } = await apiClient.pollMessages(conversationId, lastPollIso);
      for (const m of incoming) {
        const role: Message['role'] = (m.role as Message['role']) ?? 'agent';
        // Always advance the cursor so we don't re-pull the same row.
        if (m.created_at > lastPollIso) lastPollIso = m.created_at;
        // Skip own-customer echoes — they were already rendered locally
        // when the user typed; re-appending the DB row would surface a
        // duplicate bubble of the user's own question.
        if (role === 'customer') continue;
        const inserted = appendMessage({
          id: m.id,
          role,
          text: m.text,
          attachments: [],
          created_at: m.created_at,
        });
        if (inserted) {
          if (!isOpen) unread += 1;
          maybePlayReplySound(role);
        }
      }
    } catch {
      /* swallow — retry next tick */
    }
  }

  // ---- UI actions -----------------------------------------------------------

  // Welcome bubble lifecycle.
  //   - Show once the panel is closed AND a non-empty message is configured
  //     AND the user hasn't dismissed it manually.
  //   - Schedule auto-hide based on `welcome_timeout` (seconds). `0` (or
  //     missing) keeps it pinned until the user opens the chat or closes it.
  //   - The timer handle is captured by the cleanup closure so Svelte
  //     guarantees clearTimeout on every re-run AND on component unmount,
  //     with no shared module-level state to drift out of sync.
  $effect(() => {
    const message = (branding.welcome_message ?? '').trim();
    const eligible =
      !dismissed &&
      !isOpen &&
      !welcomeDismissed &&
      message.length > 0 &&
      !isLoading &&
      view !== 'error';

    if (!eligible) {
      welcomeVisible = false;
      return;
    }

    welcomeVisible = true;
    const seconds = branding.welcome_timeout;
    if (typeof seconds !== 'number' || seconds <= 0) return;

    const id = setTimeout(
      () => {
        welcomeVisible = false;
        welcomeDismissed = true;
      },
      Math.min(seconds, 300) * 1000,
    );
    return () => clearTimeout(id);
  });

  function dismissWelcomeBubble(): void {
    welcomeVisible = false;
    welcomeDismissed = true;
    if (welcomeDismissTimer) clearTimeout(welcomeDismissTimer);
    welcomeDismissTimer = setTimeout(() => {
      welcomeDismissed = false;
      welcomeDismissTimer = null;
    }, TEMPORARY_DISMISS_MS);
  }

  /**
   * Hide the entire widget for a short cooldown. This lets the visitor reach
   * host-page controls the FAB may be covering without permanently disabling
   * the widget for the rest of the visit.
   */
  function dismissWidget(): void {
    dismissed = true;
    welcomeVisible = false;
    welcomeDismissed = true;
    if (widgetDismissTimer) clearTimeout(widgetDismissTimer);
    if (welcomeDismissTimer) {
      clearTimeout(welcomeDismissTimer);
      welcomeDismissTimer = null;
    }
    widgetDismissTimer = setTimeout(() => {
      dismissed = false;
      widgetDismissTimer = null;
    }, TEMPORARY_DISMISS_MS);
  }

  function toggleOpen(): void {
    isOpen = !isOpen;
    if (isOpen) {
      unread = 0;
      // Opening the panel implicitly retires the welcome bubble; the $effect
      // sees `isOpen` flip and runs its cleanup, which clears the timer.
      welcomeVisible = false;
      welcomeDismissed = true;
    }
  }

  function closePanel(): void {
    isOpen = false;
  }

  function startChat(): void {
    view = 'chat';
  }

  /**
   * Click handler for welcome chips. Routes the chip's label as the user's
   * first message — the AI replies in-thread through the normal SSE flow.
   * We move to chat first so the customer sees the bubble immediately.
   */
  function handleChipSelect(option: string): void {
    view = 'chat';
    void sendMessage(option);
  }

  // Welcome-chips config resolved with safe defaults — the schema defines
  // every field as optional/optional-with-default, so we still need to
  // gate the render on `enabled` + non-empty options/question here.
  const welcomeChipsConfig = $derived(branding.welcome_chips);
  const showWelcomeChips = $derived(
    !!welcomeChipsConfig?.enabled &&
      (welcomeChipsConfig?.options ?? []).length > 0 &&
      (welcomeChipsConfig?.question ?? '').trim().length > 0,
  );
  const hideFaqsForChips = $derived(
    showWelcomeChips && welcomeChipsConfig?.hide_faqs === true,
  );

  /**
   * Entry point when the customer clicks "Talk to a human" or when the server
   * flags the message with `needsHumanHandoff`. Flow:
   *   1. If the tenant has no enabled handoff fields at all → handoff
   *      immediately with whatever identity we already have.
   *   2. If mode = 'form' → always show the dedicated form so the tenant
   *      can collect / confirm every declared field, even when the required
   *      ones are already covered (this is the "I want the form every time"
   *      configuration).
   *   3. If mode = 'inline' (default) → skip to immediate handoff when every
   *      required field is covered; otherwise ask for the missing ones
   *      through chat messages.
   */
  function startHandoff(): void {
    const mode = capture?.handoff?.mode ?? 'form';

    // If the tenant configured a script to run on handoff and it hasn't fired
    // yet, run the script first. When it finishes, `completeScript` calls
    // `startHandoff` again (with `scriptState.completed = true`) and we fall
    // through to the regular handoff flow.
    if (
      scriptEnabled &&
      scriptConfig?.triggers?.onHandoff &&
      !scriptState.completed &&
      !scriptActive
    ) {
      startScript('on_handoff');
      return;
    }

    if (!apiClient || !conversationId) {
      humanConnecting = true;
      view = 'connecting';
      return;
    }
    handoffError = null;

    // Nothing configured → fire-and-forget with whatever we know.
    if (!handoffFormAvailable) {
      void performHandoff(seededContact);
      return;
    }

    // Already collecting — another handoff trigger during collection would
    // just restart, so we keep the current state.
    if (handoffCollecting || view === 'handoff_form') return;

    if (mode === 'form') {
      // Form mode is explicit about wanting to see the form every time;
      // seededContact pre-fills what we already know.
      view = 'handoff_form';
      return;
    }
    // Inline mode: only interrupt if required data is missing.
    if (allRequiredFieldsCovered) {
      void performHandoff(seededContact);
      return;
    }
    beginInlineHandoff();
  }

  /**
   * Required-and-not-yet-collected fields, in a stable display order.
   * Returns built-in keys first (in canonical order) then custom keys
   * appended in the order the tenant configured them.
   */
  function missingHandoffFields(collected: HandoffContact): string[] {
    const order: CaptureFieldName[] = ['name', 'email', 'phone', 'business'];
    const fields = capture?.handoff?.fields ?? [];
    const builtinMissing = order.filter((key) => {
      const f = fields.find((x) => x.field === key);
      if (!f || !f.enabled || !f.required) return false;
      return !collected[key] || collected[key]?.trim().length === 0;
    });
    const customMissing = fields
      .filter(
        (f) =>
          f.enabled &&
          f.required &&
          !isBuiltinHandoffField(f.field) &&
          !(collected.custom?.[f.field]?.trim().length ?? 0),
      )
      .map((f) => f.field);
    return [...builtinMissing, ...customMissing];
  }

  /** Human-friendly Spanish/English label for a single field. */
  function fieldPrompt(key: string): string {
    const en = activeLocale === 'en';
    if (key === 'name') return en ? 'your name' : 'tu nombre';
    if (key === 'email') return en ? 'your email' : 'tu correo';
    if (key === 'phone') return en ? 'your phone number' : 'tu teléfono';
    if (key === 'business') return en ? 'your business name' : 'el nombre de tu negocio';
    // For custom fields, fall back to the configured label so the inline
    // prompt still reads naturally (e.g. "tu DNI", "tu RUC").
    const cfg = capture?.handoff?.fields?.find((f) => f.field === key);
    if (cfg?.label) return cfg.label;
    return key;
  }

  /** Localised "please share X and Y before I connect you" prompt. */
  function handoffAskMessage(missing: string[]): string {
    if (missing.length === 0) return '';
    const labels = missing.map(fieldPrompt);
    const joiner = activeLocale === 'en' ? ' and ' : ' y ';
    const list =
      labels.length <= 1
        ? (labels[0] ?? '')
        : labels.slice(0, -1).join(', ') + joiner + labels[labels.length - 1];
    return activeLocale === 'en'
      ? `Before I connect you with an agent, could you share ${list}?`
      : `Antes de pasarte con un asesor, ¿me compartes ${list}?`;
  }

  function beginInlineHandoff(): void {
    const seed = seededContact;
    handoffCollecting = seed;
    const missing = missingHandoffFields(seed);
    if (missing.length === 0) {
      void performHandoff(seed);
      handoffCollecting = null;
      return;
    }
    appendSynthAi(handoffAskMessage(missing));
  }

  /**
   * Parse free-form user text into contact fields. Email + phone are
   * extracted by regex; whatever's left is treated as the name (trimmed of
   * common "soy", "mi nombre es" leading filler). No business heuristic —
   * business only fills if the user types it explicitly as email/phone.
   */
  function parseContactFromText(text: string): HandoffContact {
    const emailMatch = text.match(/[\w.+-]+@[\w-]+(?:\.[\w-]+)+/);
    const phoneMatch = text.match(/(?:\+?\d[\d\s().-]{6,}\d)/);
    let residual = text;
    if (emailMatch) residual = residual.replace(emailMatch[0], ' ');
    if (phoneMatch) residual = residual.replace(phoneMatch[0], ' ');
    const name = residual
      .replace(/\s+/g, ' ')
      .replace(/^\s*(?:soy|me\s+llamo|mi\s+nombre\s+es|i\s*am|my\s+name\s+is)\s+/i, '')
      .trim()
      .replace(/[\s,;.:]+$/g, '');
    const out: HandoffContact = {};
    if (name) out.name = name;
    if (emailMatch) out.email = emailMatch[0];
    if (phoneMatch) out.phone = phoneMatch[0].replace(/\s+/g, ' ').trim();
    return out;
  }

  /** Merges new values into the running handoff state, skipping blanks. */
  function mergeContact(a: HandoffContact, b: HandoffContact): HandoffContact {
    const merged: HandoffContact = { ...a };
    for (const key of ['name', 'email', 'phone', 'business'] as const) {
      const v = b[key];
      if (v && v.trim().length > 0) merged[key] = v.trim();
    }
    return merged;
  }

  /** Appends a synthetic AI message to the thread (no network call). */
  function appendSynthAi(text: string): void {
    messages = [
      ...messages,
      {
        id: cryptoRandomId(),
        role: 'ai',
        text,
        attachments: [],
        created_at: new Date().toISOString(),
        status: 'sent',
      },
    ];
  }

  /**
   * Is a free-text user reply an affirmative answer to "¿querés que te pase
   * con un asesor?". Conservative: short, unambiguous yes-like replies.
   * Non-affirmatives (or anything off-topic) clear the pending proposal so
   * the conversation goes back to the normal AI flow.
   */
  /**
   * Evaluate the latest turn for escalation signals and, if any fire,
   * append a synthetic "¿Quieres que te pase con un asesor?" message with
   * Yes/No buttons. One offer per session so we never spam. The user's
   * explicit request path (regex match on their message) is handled
   * elsewhere and is stronger — it triggers handoff directly, no prompt.
   */
  function maybeOfferHandoff(
    userMessage: string,
    aiAnswer: string,
    aiConfidence: number | null,
  ): void {
    if (handoffOfferShownThisSession || handoffOfferPending) return;
    if (view === 'handoff_form' || view === 'connecting' || humanConnecting) return;
    if (handoffCollecting) return;
    if (!capture?.handoff?.enabled) return;

    const decision = decideOfferHandoff({
      userMessage,
      lastAiAnswer: aiAnswer,
      customerTurnCount,
      lastAiConfidence: aiConfidence,
    });
    if (!decision.shouldOffer) return;

    handoffOfferPending = true;
    handoffOfferShownThisSession = true;
    appendSynthAi(
      activeLocale === 'en'
        ? 'It looks like I might not be solving this for you. Would you like me to connect you with a human agent?'
        : 'Me parece que no te estoy resolviendo del todo. ¿Quieres que te pase con un asesor humano?',
    );
  }

  function acceptHandoffOffer(): void {
    handoffOfferPending = false;
    appendSynthCustomer(activeLocale === 'en' ? 'Yes, please' : 'Sí, por favor');
    startHandoff();
  }

  function declineHandoffOffer(): void {
    handoffOfferPending = false;
    appendSynthCustomer(
      activeLocale === 'en' ? 'No, keep trying' : 'No, sigue intentando',
    );
    appendSynthAi(
      activeLocale === 'en'
        ? 'Got it — I\'ll keep trying to help. What else can I check?'
        : 'Perfecto, sigo intentando. ¿Qué más te gustaría que revise?',
    );
  }

  function isAffirmativeReply(text: string): boolean {
    const t = text.trim().toLowerCase();
    if (t.length === 0) return false;
    // Allow short replies up to ~30 chars — anything longer is almost
    // certainly a new question, not a confirmation.
    if (t.length > 30) return false;
    return /^(s[ií][ií\s,.!]*(?:por\s+favor|gracias|claro)?|claro|dale|ok(?:ay)?|okey|bueno|por\s+favor|hazlo|pasa(?:me)?|conect(?:a|ame)|quiero|yes|yep|yeah|please|sure|go\s+ahead|connect\s+me)[\s.!¡]*$/i.test(
      t,
    );
  }

  function isNegativeReply(text: string): boolean {
    const t = text.trim().toLowerCase();
    if (t.length === 0) return false;
    if (t.length > 30) return false;
    return /^(no+[\s,.!]*|nop(?:e)?|todav[íi]a\s+no|ahora\s+no|mejor\s+no|luego|despu[eé]s|cancel(?:a|ar)?|olvid[aá]lo|later|not\s+now|never\s?mind)[\s.!¡]*$/i.test(
      t,
    );
  }

  /** Appends a synthetic customer message (used by scripted choice clicks). */
  function appendSynthCustomer(text: string): void {
    messages = [
      ...messages,
      {
        id: cryptoRandomId(),
        role: 'customer',
        text,
        attachments: [],
        created_at: new Date().toISOString(),
        status: 'sent',
      },
    ];
  }

  // ---- Scripted flow engine -----------------------------------------------

  /**
   * Start the scripted flow for the given trigger. No-ops when the script
   * isn't enabled, has already run this session, or the trigger doesn't
   * match the tenant's configuration.
   */
  function startScript(trigger: ScriptTrigger): void {
    if (!scriptEnabled || !scriptConfig || !scriptConfig.firstStepId) return;
    if (scriptState.completed || scriptState.currentStepId) return;
    const triggers = scriptConfig.triggers;
    const allowed =
      (trigger === 'on_open' && triggers.onOpen) ||
      (trigger === 'on_handoff' && triggers.onHandoff);
    if (!allowed) return;

    const firstStep = scriptConfig.steps.find((s) => s.id === scriptConfig.firstStepId);
    if (!firstStep) return;

    scriptState = {
      currentStepId: firstStep.id,
      answers: {},
      trigger,
      completed: false,
      submitting: false,
      handoffPending: trigger === 'on_handoff' || scriptConfig.handoffAfter,
    };
    view = 'chat';
    appendSynthAi(firstStep.prompt);
  }

  /**
   * Advance the script with the user's answer. Records the answer under the
   * step's `answerKey`, appends the user message to the thread, jumps to
   * `next` (or the choice's `nextStepId` when the source step is 'choice'),
   * and either shows the next prompt or ends the flow.
   */
  async function submitScriptAnswer(
    answerText: string,
    opts: { nextStepId: string | null; appendUser: boolean },
  ): Promise<void> {
    if (!scriptConfig || !currentScriptStep || scriptState.submitting) return;

    const trimmed = answerText.trim();
    if (trimmed.length === 0) return;

    if (opts.appendUser) appendSynthCustomer(trimmed);

    const nextAnswers: Record<string, string> = {
      ...scriptState.answers,
      [currentScriptStep.answerKey]: trimmed,
    };

    if (opts.nextStepId === null) {
      await completeScript(nextAnswers);
      return;
    }

    const nextStep = scriptConfig.steps.find((s) => s.id === opts.nextStepId);
    if (!nextStep) {
      // Broken config — treat as end of script.
      await completeScript(nextAnswers);
      return;
    }

    scriptState = {
      ...scriptState,
      currentStepId: nextStep.id,
      answers: nextAnswers,
    };
    appendSynthAi(nextStep.prompt);
  }

  /** Submit final answers, show closing, optionally trigger handoff. */
  async function completeScript(finalAnswers: Record<string, string>): Promise<void> {
    if (!scriptConfig || !apiClient) return;
    scriptState = { ...scriptState, submitting: true, answers: finalAnswers };

    // Ensure we have a conversation id to attach the lead to. The chat API
    // creates one on first message; scripts can run before any AI message
    // so we create one up front through a no-op message if needed.
    try {
      if (!conversationId) {
        // Kick off a lightweight session-verifying call: sending an empty
        // synthetic first message is overkill, so we just POST to the
        // script endpoint with null conversation_id and let the server
        // mint one. See /api/widget/script-complete.
      }

      const trigger = scriptState.trigger ?? 'on_open';
      const { conversationId: returnedId } = await apiClient.scriptComplete({
        conversationId,
        answers: finalAnswers,
        trigger,
      });
      conversationId = returnedId;

      if (scriptConfig.closing) {
        appendSynthAi(scriptConfig.closing);
      }

      const shouldHandoff =
        scriptState.handoffPending || scriptConfig.handoffAfter;

      scriptState = {
        currentStepId: null,
        answers: finalAnswers,
        trigger: scriptState.trigger,
        completed: true,
        submitting: false,
        handoffPending: false,
      };

      if (shouldHandoff) {
        // Reuse the regular handoff flow so the tenant's capture-form / inline
        // collection still fires if configured. The collected script answers
        // are already on the lead record, so the handoff form only gathers
        // identity info the script didn't ask about.
        startHandoff();
      }
    } catch (err) {
      scriptState = { ...scriptState, submitting: false };
      appendSynthAi(
        activeLocale === 'en'
          ? 'Sorry, we could not save your answers. Please try again.'
          : 'Lo siento, no pudimos guardar tus respuestas. Intenta de nuevo.',
      );
      console.error('[helpdesk-widget] script-complete failed', err);
    }
  }

  /** Choice button handler — posts the label as the answer. */
  function handleScriptChoice(choice: { label: string; nextStepId: string | null }): void {
    void submitScriptAnswer(choice.label, {
      nextStepId: choice.nextStepId,
      appendUser: true,
    });
  }

  /**
   * Submits the handoff to the backend with whatever identity info we have
   * (either from the form, from `sourced`, or both — form values win over
   * sourced backup values).
   */
  async function performHandoff(contact: HandoffContact): Promise<void> {
    if (!apiClient || !conversationId) return;
    handoffSubmitting = true;
    handoffError = null;
    try {
      const merged: HandoffContact = {
        name: contact.name ?? sourced.name,
        email: contact.email ?? sourced.email,
        business: contact.business ?? sourced.business,
        phone: contact.phone,
      };
      const cleaned = Object.fromEntries(
        Object.entries(merged).filter(([, v]) => v != null && v !== ''),
      ) as HandoffContact;

      humanConnecting = true;
      view = 'connecting';
      // Reassuring ACK so the customer sees a confirmation in the chat
      // thread the moment they submit the form / inline data. Without
      // this the UI just flips to the "connecting" spinner and the user
      // is unsure whether anything actually happened.
      appendSynthAi(
        activeLocale === 'en'
          ? "Thanks — we're connecting you with someone from our team. One moment please."
          : 'Gracias. Te estamos contactando con un asistente de nuestro equipo, un momento por favor.',
      );
      await apiClient.requestHuman(conversationId, {
        contact: Object.keys(cleaned).length > 0 ? cleaned : undefined,
      });
    } catch (err) {
      humanConnecting = false;
      if (view === 'connecting') {
        // Went from form → connecting → error: keep user near the form so
        // they can retry with the same data.
        handoffError = errorToMessage(err, activeLocale);
        view = handoffFormAvailable ? 'handoff_form' : 'chat';
      } else {
        errorMessage = errorToMessage(err, activeLocale);
        view = 'error';
      }
    } finally {
      handoffSubmitting = false;
    }
  }

  /**
   * Evaluate whether lead capture should fire now. Called after each AI
   * turn. Current triggers:
   *   - `after_n_turns`: show form once the customer has sent >= threshold
   *     messages. `ai_decision` and `on_resolve` live outside this helper
   *     and are wired elsewhere (tool-call / resolve event).
   */
  function maybeTriggerLeadCapture(): void {
    if (leadCaptured || leadSubmitting) return;
    if (!leadCaptureAvailable || !capture?.leads) return;
    // Don't interrupt a pending handoff.
    if (view === 'handoff_form' || view === 'connecting') return;

    if (capture.leads.trigger === 'after_n_turns') {
      const threshold = capture.leads.turnThreshold ?? 3;
      if (customerTurnCount >= threshold) {
        startLeadCapture();
      }
    }
  }

  function startLeadCapture(): void {
    if (leadCaptured || !leadCaptureAvailable) return;
    leadError = null;
    view = 'lead_capture_form';
  }

  async function performLeadCapture(fields: Record<string, string>): Promise<void> {
    if (!apiClient || !conversationId) return;
    if (leadSubmitting) return;
    leadSubmitting = true;
    leadError = null;
    try {
      const trigger = capture?.leads?.trigger ?? 'ai_decision';
      const source = trigger === 'on_resolve' ? 'on_resolve' : trigger === 'after_n_turns' ? 'after_n_turns' : 'ai_decision';
      const result = await apiClient.captureLead(conversationId, fields, source);
      leadCaptured = true;
      view = 'chat';
      if (result.closeMessage) {
        appendSystem(result.closeMessage);
      }
    } catch (err) {
      leadError = errorToMessage(err, activeLocale);
    } finally {
      leadSubmitting = false;
    }
  }

  /** Let the user postpone / decline the lead form without blocking the chat. */
  function cancelLeadCapture(): void {
    // Mark as captured so we don't re-prompt on every subsequent turn. The
    // agent can still capture manually from the inbox.
    leadCaptured = true;
    leadError = null;
    view = 'chat';
  }

  async function handleAttach(files: FileList): Promise<void> {
    if (!apiClient) return;
    for (const file of Array.from(files)) {
      if (file.size > MAX_ATTACHMENT_BYTES) {
        errorMessage = t(activeLocale, 'error.attachment_too_big', {
          mb: Math.round(MAX_ATTACHMENT_BYTES / 1024 / 1024),
        });
        continue;
      }
      try {
        const presign = await apiClient.presignUpload({
          name: file.name,
          type: file.type || 'application/octet-stream',
          size: file.size,
        });
        await apiClient.uploadToPresignedUrl(presign.uploadUrl, file);
        const previewUrl = file.type.startsWith('image/')
          ? URL.createObjectURL(file)
          : undefined;
        if (previewUrl) pendingFileUrls.set(presign.storage_key, previewUrl);
        pendingAttachments = [
          ...pendingAttachments,
          {
            storage_key: presign.storage_key,
            content_type: file.type || 'application/octet-stream',
            size_bytes: file.size,
            original_name: file.name,
            preview_url: previewUrl,
          },
        ];
      } catch (err) {
        errorMessage = errorToMessage(err, activeLocale);
      }
    }
  }

  function removeAttachment(att: Attachment): void {
    const url = pendingFileUrls.get(att.storage_key);
    if (url) {
      URL.revokeObjectURL(url);
      pendingFileUrls.delete(att.storage_key);
    }
    pendingAttachments = pendingAttachments.filter((a) => a.storage_key !== att.storage_key);
  }

  async function sendMessage(text: string): Promise<void> {
    if (!apiClient) return;
    if (serviceBlocked) return;

    // Scripted flow short-circuit: free text typed into the composer should
    // advance the current script step even when the UI also offers preset
    // choice buttons. Typed text on a `choice` step follows the step's
    // default `nextStepId`; clicking a preset choice still uses that
    // choice's explicit `nextStepId`.
    if (scriptActive && currentScriptStep) {
      void submitScriptAnswer(text, {
        nextStepId: currentScriptStep.nextStepId,
        appendUser: true,
      });
      return;
    }

    // PENDING OFFER RESOLUTION — if we already asked the user "¿quieres
    // que te pase con un asesor?", treat short yes/no replies as a click
    // on the Yes/No buttons. Anything else clears the offer and goes
    // through the normal AI pipeline (the user changed subject).
    if (handoffOfferPending) {
      if (isAffirmativeReply(text)) {
        appendSynthCustomer(text);
        handoffOfferPending = false;
        startHandoff();
        return;
      }
      if (isNegativeReply(text)) {
        handoffOfferPending = false;
        declineHandoffOffer();
        return;
      }
      handoffOfferPending = false;
      // fall through to normal send
    }

    // EXPLICIT HANDOFF SHORT-CIRCUIT
    //
    // Handing the user to a human agent is the alma of the product — it
    // MUST be deterministic and completely independent of the LLM. When
    // the user's message matches the human-intent regex, we:
    //   1. Record the user message locally (no chat API call).
    //   2. Emit a short, plain ACK so the thread doesn't look frozen.
    //   3. Persist both turns server-side via requestHuman's flow (handled
    //      inside `startHandoff` → `performHandoff`).
    //   4. Trigger the handoff flow immediately.
    //
    // We do NOT stream anything through the AI model for this turn, so
    // the model's choice (gpt-4o-mini, Claude, Gemini, whatever) cannot
    // refuse, stall, or swallow the request. This is the root-cause fix:
    // previous attempts relied on the server's regex flag, which can be
    // defeated by a stale build or a provider that drops the JSON envelope.
    if (wantsHumanAgent(text)) {
      messages = [
        ...messages,
        {
          id: cryptoRandomId(),
          role: 'customer',
          text,
          attachments: [],
          created_at: new Date().toISOString(),
          status: 'sent',
        },
      ];
      customerTurnCount = customerTurnCount + 1;
      appendSynthAi(
        activeLocale === 'en'
          ? 'Of course — connecting you with a human agent now.'
          : 'Claro, te conecto con un asesor ahora mismo.',
      );
      view = 'chat';
      startHandoff();
      return;
    }

    // Handoff proposal support is disabled under the explicit-only policy.
    // We keep the state around so older server builds that still send
    // `proposeHandoff: true` don't produce undefined behaviour — but the
    // flag is never read here anymore. The only way a handoff fires is the
    // server seeing an explicit regex match on this message, which bubbles
    // up as `evt.meta.needsHumanHandoff` in the done event.
    if (handoffProposalPending) handoffProposalPending = false;

    // Inline handoff collection: short-circuit the chat API and parse the
    // user's reply for the fields we still need. Only once every required
    // field is filled do we fire the handoff request.
    if (handoffCollecting) {
      const collected = mergeContact(handoffCollecting, parseContactFromText(text));
      handoffCollecting = collected;
      messages = [
        ...messages,
        {
          id: cryptoRandomId(),
          role: 'customer',
          text,
          attachments: [],
          created_at: new Date().toISOString(),
          status: 'sent',
        },
      ];
      const missing = missingHandoffFields(collected);
      if (missing.length === 0) {
        appendSynthAi(
          activeLocale === 'en'
            ? 'Thanks — connecting you with an agent now.'
            : 'Gracias. Te conecto con un asesor ahora.',
        );
        handoffCollecting = null;
        await performHandoff(collected);
      } else {
        appendSynthAi(
          activeLocale === 'en'
            ? `Got it. I still need ${missing.map(fieldPrompt).join(' and ')}.`
            : `Perfecto. Aún me falta ${missing.map(fieldPrompt).join(' y ')}.`,
        );
      }
      return;
    }

    const attachmentsToSend = pendingAttachments;
    pendingAttachments = [];

    const localId = cryptoRandomId();
    const localMsg: Message = {
      id: localId,
      role: 'customer',
      text,
      attachments: attachmentsToSend,
      created_at: new Date().toISOString(),
      status: 'sending',
    };
    messages = [...messages, localMsg];
    customerTurnCount = customerTurnCount + 1;
    view = 'chat';

    let aiBuffer = '';
    isTyping = true;

    streamAbort?.abort();
    streamAbort = new AbortController();

    try {
      for await (const evt of apiClient.chatStream(
        {
          conversationId,
          message: text,
          attachments: attachmentsToSend.map(({ preview_url, ...rest }) => rest),
          page: collectPage(),
        },
        streamAbort.signal,
      )) {
        if (evt.type === 'delta') {
          aiBuffer += evt.text;
        } else if (evt.type === 'done') {
          conversationId = evt.meta.conversation_id;
          messages = updateMessageById(messages, localId, (m) => ({ ...m, status: 'sent' }));
          appendMessage({
            id: evt.meta.message_id ?? cryptoRandomId(),
            role: 'ai',
            text: aiBuffer.trim().length > 0 ? aiBuffer : t(activeLocale, 'error.generic'),
            attachments: [],
            created_at: new Date().toISOString(),
            status: aiBuffer.trim().length > 0 ? 'sent' : 'failed',
            confidence: evt.meta.confidence,
          });
          // The AI's final answer just landed — beep once. (Streaming
          // deltas are silent so we don't fire the chime mid-token.)
          maybePlayReplySound('ai');
          // Under the explicit-only handoff policy, `needsHumanHandoff`
          // only arrives when the user's LAST message matched the server-
          // side regex for "asesor"/"humano"/"agente"/etc. We trust the
          // server and obey. `proposeHandoff` is ignored — the feature is
          // disabled until we re-introduce it as an opt-in tenant setting.
          //
          // Defensive fallback: if the server somehow omits the flag (stale
          // build, provider drops JSON envelope, etc.) but the user's own
          // message clearly asked for a human, trigger handoff anyway. The
          // client regex mirrors the server one — staying silent here left
          // users stuck reading "no puedo conectar con un agente" from the
          // LLM after explicitly asking for one.
          const clientWantsHuman = wantsHumanAgent(text);
          if (evt.meta.needsHumanHandoff || clientWantsHuman) {
            startHandoff();
          } else {
            // Evaluate lead-capture triggers only when we didn't already hand
            // off to a human — capturing a lead in the middle of a handoff
            // would be noisy.
            maybeTriggerLeadCapture();
            // PROACTIVE OFFER — if the AI looks like it's failing the user
            // (frustration phrase / bot refusal / low-confidence repetition),
            // surface a one-click "¿Quieres que te pase con un asesor?" so
            // the user doesn't have to guess a magic phrase. Only show once
            // per session to avoid nagging.
            maybeOfferHandoff(text, aiBuffer, evt.meta.confidence ?? null);
          }
        } else if (evt.type === 'blocked') {
          // Tenant exhausted their plan quota mid-conversation. Drop the
          // pending bubble back to "failed" and show the soft block notice.
          serviceBlocked = true;
          messages = updateMessageById(messages, localId, (m) => ({ ...m, status: 'failed' }));
          appendSystem(t(activeLocale, 'block.body'));
          break;
        } else if (evt.type === 'error') {
          throw new Error(evt.error);
        }
      }
    } catch (err) {
      messages = updateMessageById(messages, localId, (m) => ({ ...m, status: 'failed' }));
      appendMessage({
        id: cryptoRandomId(),
        role: 'ai',
        text: aiBuffer || t(activeLocale, 'error.generic'),
        attachments: [],
        created_at: new Date().toISOString(),
        status: 'failed',
      });
      errorMessage = errorToMessage(err, activeLocale);
    } finally {
      isTyping = false;
      // Set up realtime now that we (likely) have a conversationId
      if (conversationId && (!socket || !socket.connected)) {
        setupRealtime();
      }
    }
  }

  // ---- Helpers --------------------------------------------------------------

  /**
   * Append a message to the thread. Returns false when the id already
   * exists so callers (e.g. realtime + polling) can avoid double-counting
   * unread / playing the chime twice for the same delivery.
   */
  function appendMessage(msg: Message): boolean {
    if (messages.some((m) => m.id === msg.id)) return false;
    messages = [...messages, msg];
    return true;
  }

  function appendSystem(text: string): void {
    messages = [
      ...messages,
      {
        id: cryptoRandomId(),
        role: 'system',
        text,
        attachments: [],
        created_at: new Date().toISOString(),
      },
    ];
  }

  function updateMessageById(
    list: Message[],
    id: string,
    update: (m: Message) => Message,
  ): Message[] {
    return list.map((m) => (m.id === id ? update(m) : m));
  }

  function cryptoRandomId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `hd_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
  }

  function errorToMessage(err: unknown, loc: Locale): string {
    if (err instanceof ApiError) return err.message;
    if (err instanceof Error) return err.message;
    return t(loc, 'error.generic');
  }

  function buildBrandingStyle(b: Branding): string {
    const primary = b.primary_color ?? DEFAULT_PRIMARY;
    const primaryRgb = hexToRgb(primary) ?? '37, 99, 235';
    // A light contrast for primary-fg — assume white for coloured backgrounds
    return [
      `--hd-primary: ${primary};`,
      `--hd-primary-rgb: ${primaryRgb};`,
      `--hd-primary-fg: #ffffff;`,
      `--hd-surface: #ffffff;`,
      `--hd-surface-muted: #f8fafc;`,
      `--hd-surface-subtle: #f1f5f9;`,
      `--hd-fg: #0f172a;`,
      `--hd-fg-muted: #64748b;`,
      `--hd-fg-subtle: #94a3b8;`,
      `--hd-border: #e2e8f0;`,
    ].join(' ');
  }

  function hexToRgb(hex: string): string | null {
    const normalised = hex.trim().replace('#', '');
    const full =
      normalised.length === 3
        ? normalised
            .split('')
            .map((c) => c + c)
            .join('')
        : normalised;
    if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `${r}, ${g}, ${b}`;
  }

  function buildPositionStyle(pos: WidgetPosition): string {
    switch (pos) {
      case 'bottom-left':
        return 'bottom: 20px; left: 20px; align-items: flex-start;';
      case 'top-right':
        return 'top: 20px; right: 20px; align-items: flex-end;';
      case 'top-left':
        return 'top: 20px; left: 20px; align-items: flex-start;';
      case 'bottom-right':
      default:
        return 'bottom: 20px; right: 20px; align-items: flex-end;';
    }
  }

  const headerTitle = $derived(branding.greeting ?? t(activeLocale, 'widget.greeting'));

  // If a tenant only provides an assistant avatar, use it as the header logo
  // as well so the persona photo shows everywhere without extra config.
  const effectiveLogoUrl = $derived(branding.logo_url ?? branding.assistant_avatar_url);
  const assistantAvatarForBubbles = $derived(
    branding.assistant_avatar_url ?? branding.logo_url,
  );
</script>

<div class="hd-root" style={brandingStyle + ' ' + positionStyle}>
  {#if !dismissed}
  {#if isOpen}
    <div class="hd-panel-wrap">
      <Panel
        title={headerTitle}
        subtitle={t(activeLocale, 'widget.subtitle')}
        logoUrl={effectiveLogoUrl}
        assistantName={branding.assistant_name}
        assistantAvatarUrl={branding.assistant_avatar_url}
        assistantTagline={branding.assistant_tagline}
        closeLabel={t(activeLocale, 'widget.close')}
        onclose={closePanel}
        showRequestHuman={view === 'chat' && !agentJoined && !humanConnecting}
        requestHumanLabel={t(activeLocale, 'widget.requestHuman')}
        onrequesthuman={startHandoff}
        showSoundToggle={branding.notification_sound !== false}
        soundMuted={soundMuted}
        onTogglesound={() => (soundMuted = !soundMuted)}
        soundOnLabel={t(activeLocale, 'widget.soundOn')}
        soundOffLabel={t(activeLocale, 'widget.soundOff')}
        footerLabel={t(activeLocale, 'widget.poweredBy')}
        footerHref={SERVICE_LANDING_URL}
        footerLinkAriaLabel={t(activeLocale, 'widget.poweredByLinkAria')}
      >
        {#if isLoading}
          <div class="hd-state">
            <span class="hd-state__dots" aria-hidden="true">
              <span></span><span></span><span></span>
            </span>
          </div>
        {:else if view === 'error'}
          <div class="hd-state hd-state--error" role="alert">
            {errorMessage ?? t(activeLocale, 'error.generic')}
          </div>
        {:else if view === 'connecting'}
          <div class="hd-state" role="status" aria-live="polite">
            {t(activeLocale, 'chat.connecting')}
          </div>
          <ChatThread
            {messages}
            isTyping={false}
            assistantAvatarUrl={assistantAvatarForBubbles}
            assistantName={branding.assistant_name}
          />
          <Composer
            placeholder={t(activeLocale, 'chat.placeholder')}
            sendLabel={t(activeLocale, 'chat.send')}
            attachLabel={t(activeLocale, 'chat.attach')}
            enterToSendLabel={t(activeLocale, 'chat.enter_to_send')}
            showAttach={branding.allow_attachments !== false}
            {pendingAttachments}
            onsend={sendMessage}
            onattach={handleAttach}
            onremoveattachment={removeAttachment}
          />
        {:else if view === 'faq'}
          {#if showWelcomeChips}
            <WelcomeChips
              question={welcomeChipsConfig?.question ?? ''}
              options={welcomeChipsConfig?.options ?? []}
              assistantName={branding.assistant_name}
              assistantAvatarUrl={branding.assistant_avatar_url}
              onselect={handleChipSelect}
            />
          {/if}
          {#if !hideFaqsForChips}
            <FaqList
              {faqs}
              searchPlaceholder={t(activeLocale, 'faq.search')}
              emptyLabel={t(activeLocale, 'faq.empty')}
              startChatLabel={t(activeLocale, 'widget.startChat')}
              welcomeTitle={t(activeLocale, 'widget.welcomeTitle')}
              welcomePrompt={t(activeLocale, 'widget.welcomePrompt')}
              assistantName={branding.assistant_name}
              assistantAvatarUrl={branding.assistant_avatar_url}
              assistantTagline={branding.assistant_tagline}
              onstartchat={startChat}
            />
          {/if}
        {:else if view === 'chat'}
          <ChatThread
            {messages}
            {isTyping}
            typingLabel={t(activeLocale, 'chat.typing')}
            assistantAvatarUrl={assistantAvatarForBubbles}
            assistantName={branding.assistant_name}
          />
          {#if serviceBlocked}
            <div class="hd-blocked" role="status" aria-live="polite">
              <p class="hd-blocked-title">{t(activeLocale, 'block.title')}</p>
              <p class="hd-blocked-body">{t(activeLocale, 'block.body')}</p>
            </div>
          {/if}
          {#if scriptAwaitingChoice && currentScriptStep}
            <ScriptChoices
              choices={currentScriptStep.choices}
              disabled={scriptState.submitting}
              onselect={handleScriptChoice}
            />
            <Composer
              placeholder={t(activeLocale, 'chat.placeholder')}
              sendLabel={t(activeLocale, 'chat.send')}
              attachLabel={t(activeLocale, 'chat.attach')}
              enterToSendLabel={t(activeLocale, 'chat.enter_to_send')}
              showAttach={branding.allow_attachments !== false}
              {pendingAttachments}
              onsend={sendMessage}
              onattach={handleAttach}
              onremoveattachment={removeAttachment}
            />
          {:else if handoffOfferPending}
            <ScriptChoices
              choices={[
                {
                  label:
                    activeLocale === 'en'
                      ? 'Yes, connect me with an agent'
                      : 'Sí, pásame con un asesor',
                  nextStepId: 'accept',
                },
                {
                  label:
                    activeLocale === 'en'
                      ? 'No, keep trying'
                      : 'No, sigue intentando',
                  nextStepId: 'decline',
                },
              ]}
              disabled={false}
              onselect={(choice) => {
                if (choice.nextStepId === 'accept') acceptHandoffOffer();
                else declineHandoffOffer();
              }}
            />
          {:else}
            <Composer
              placeholder={t(activeLocale, 'chat.placeholder')}
              sendLabel={t(activeLocale, 'chat.send')}
              attachLabel={t(activeLocale, 'chat.attach')}
              enterToSendLabel={t(activeLocale, 'chat.enter_to_send')}
              showAttach={branding.allow_attachments !== false}
              {pendingAttachments}
              onsend={sendMessage}
              onattach={handleAttach}
              onremoveattachment={removeAttachment}
            />
          {/if}
        {:else if view === 'handoff_form' && capture?.handoff}
          <HandoffForm
            config={capture.handoff}
            seed={seededContact}
            submitting={handoffSubmitting}
            error={handoffError}
            cancelLabel={t(activeLocale, 'handoff.cancel')}
            defaultTitle={t(activeLocale, 'handoff.title')}
            defaultSubmitLabel={t(activeLocale, 'handoff.submit')}
            onsubmit={(contact) => performHandoff(contact)}
            oncancel={() => {
              handoffError = null;
              view = 'chat';
            }}
          />
        {:else if view === 'lead_capture_form' && capture?.leads}
          <LeadCaptureForm
            config={capture.leads}
            submitting={leadSubmitting}
            error={leadError}
            onsubmit={(fields) => performLeadCapture(fields)}
            oncancel={cancelLeadCapture}
          />
        {/if}
      </Panel>
    </div>
  {/if}
  {#if !isOpen && welcomeVisible && (branding.welcome_message ?? '').trim().length > 0}
    <WelcomeBubble
      message={(branding.welcome_message ?? '').trim()}
      assistantName={branding.assistant_name}
      assistantAvatarUrl={branding.assistant_avatar_url}
      closeLabel={t(activeLocale, 'welcome.close')}
      onclick={() => {
        dismissWelcomeBubble();
        if (!isOpen) toggleOpen();
      }}
      onclose={dismissWelcomeBubble}
    />
  {/if}
  <div class="hd-fab-wrap">
    <FloatingButton
      open={isOpen}
      {unread}
      label={isOpen ? t(activeLocale, 'widget.close') : t(activeLocale, 'widget.open')}
      onclick={toggleOpen}
      shape={branding.widget_shape ?? 'circle'}
      size={branding.widget_size ?? 'md'}
      animation={branding.widget_animation ?? 'none'}
      imageUrl={branding.widget_image_url}
    />
    {#if !isOpen}
      <button
        type="button"
        class="hd-dismiss"
        aria-label={t(activeLocale, 'widget.dismiss')}
        title={t(activeLocale, 'widget.dismiss')}
        onclick={dismissWidget}
      >
        <svg viewBox="0 0 24 24" width="9" height="9" aria-hidden="true">
          <path
            d="M6 6l12 12M18 6L6 18"
            fill="none"
            stroke="currentColor"
            stroke-width="2.3"
            stroke-linecap="round"
          />
        </svg>
      </button>
    {/if}
  </div>
  {/if}
</div>

<style>
  :host {
    all: initial;
    display: block;
    font-family: var(
      --hd-font,
      -apple-system,
      BlinkMacSystemFont,
      'Inter',
      'Segoe UI',
      Roboto,
      'Helvetica Neue',
      Arial,
      sans-serif
    );
    font-feature-settings: 'cv11', 'ss01';
    -webkit-font-smoothing: antialiased;
    color: var(--hd-fg, #0f172a);
    line-height: 1.4;
    /* Never leak host site styles into shadow */
  }
  .hd-root {
    position: fixed;
    z-index: 2147483000;
    display: flex;
    flex-direction: column;
    gap: 14px;
    pointer-events: none;
    font-family: var(
      --hd-font,
      -apple-system,
      BlinkMacSystemFont,
      'Inter',
      'Segoe UI',
      Roboto,
      'Helvetica Neue',
      Arial,
      sans-serif
    );
    font-feature-settings: 'cv11', 'ss01';
    -webkit-font-smoothing: antialiased;
  }
  /* `:global(*)` so children rendered by sub-components (FloatingButton,
     Panel) also receive pointer-events: auto. Without :global() Svelte
     scopes the selector to this component's hash and the rule never
     matches the nested component DOM. */
  .hd-root > :global(*) {
    pointer-events: auto;
  }
  /* Wraps the FAB so the small "dismiss" button can be absolutely
     positioned relative to it without affecting layout. */
  .hd-fab-wrap {
    position: relative;
    display: inline-flex;
  }
  .hd-dismiss {
    position: absolute;
    top: -3px;
    right: -3px;
    width: 18px;
    height: 18px;
    border-radius: 9999px;
    border: none;
    cursor: pointer;
    background: color-mix(in srgb, var(--hd-fg, #0f172a) 78%, transparent);
    color: rgb(255 255 255 / 0.94);
    display: grid;
    place-items: center;
    box-shadow:
      0 6px 14px -8px rgb(15 23 42 / 0.55),
      0 0 0 1.5px var(--hd-surface, #ffffff);
    backdrop-filter: blur(6px);
    opacity: 0;
    transform: scale(0.82);
    transition:
      opacity 140ms cubic-bezier(0.2, 0.8, 0.2, 1),
      transform 140ms cubic-bezier(0.2, 0.8, 0.2, 1),
      background-color 140ms cubic-bezier(0.2, 0.8, 0.2, 1),
      box-shadow 140ms cubic-bezier(0.2, 0.8, 0.2, 1);
  }
  .hd-fab-wrap:hover .hd-dismiss,
  .hd-fab-wrap:focus-within .hd-dismiss,
  .hd-dismiss:focus-visible {
    opacity: 1;
    transform: scale(1);
  }
  .hd-dismiss:hover {
    background: color-mix(in srgb, var(--hd-fg, #0f172a) 88%, transparent);
    box-shadow:
      0 8px 16px -8px rgb(15 23 42 / 0.68),
      0 0 0 1.5px var(--hd-surface, #ffffff);
  }
  .hd-dismiss:focus-visible {
    outline: 2px solid var(--hd-primary, #2563eb);
    outline-offset: 2px;
  }
  /* Touch devices: keep the dismiss visible since hover doesn't apply. */
  @media (hover: none) {
    .hd-dismiss {
      opacity: 1;
      transform: scale(1);
    }
  }
  .hd-panel-wrap {
    width: 380px;
    max-width: calc(100vw - 32px);
    max-height: min(680px, calc(100vh - 120px));
    border-radius: 16px;
    overflow: hidden;
    background: var(--hd-surface, #ffffff);
    box-shadow:
      0 24px 48px -16px rgb(0 0 0 / 0.18),
      0 8px 16px -8px rgb(0 0 0 / 0.08),
      0 0 0 1px rgb(15 23 42 / 0.04);
    animation: hd-enter 180ms cubic-bezier(0.2, 0.8, 0.2, 1);
    transform-origin: bottom right;
    display: flex;
    flex-direction: column;
  }
  .hd-state {
    padding: 28px 24px;
    text-align: center;
    color: var(--hd-fg-muted, #64748b);
    font-size: 13px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .hd-state--error {
    color: #b91c1c;
  }
  .hd-blocked {
    margin: 8px 12px 4px;
    padding: 10px 12px;
    border-radius: 8px;
    background: var(--hd-surface-muted, #f1f5f9);
    border: 1px solid var(--hd-border, #e2e8f0);
    color: var(--hd-fg-muted, #475569);
    font-size: 12px;
    line-height: 1.45;
  }
  .hd-blocked-title {
    margin: 0 0 2px 0;
    font-weight: 600;
    color: var(--hd-fg, #0f172a);
  }
  .hd-blocked-body {
    margin: 0;
  }
  .hd-state__dots {
    display: inline-flex;
    gap: 4px;
  }
  .hd-state__dots span {
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    background: var(--hd-fg-subtle, #94a3b8);
    animation: hd-bounce 1.2s infinite ease-in-out;
  }
  .hd-state__dots span:nth-child(2) {
    animation-delay: 120ms;
  }
  .hd-state__dots span:nth-child(3) {
    animation-delay: 240ms;
  }
  @keyframes hd-enter {
    from {
      opacity: 0;
      transform: translateY(8px) scale(0.98);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
  @keyframes hd-bounce {
    0%,
    80%,
    100% {
      opacity: 0.3;
      transform: translateY(0);
    }
    40% {
      opacity: 1;
      transform: translateY(-3px);
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .hd-panel-wrap {
      animation: none;
    }
    .hd-state__dots span {
      animation: none;
    }
  }
</style>
